import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, useIsHost } from '../stores/gameStore';
import { useGameActions, useSocket } from '../hooks/useSocket';

const PenaltyView = () => {
  const { gameState, clientState } = useGameStore();
  const isHost = useIsHost();
  const { vetoPenalty, startPenaltyRoulette, playAgain } = useGameActions();
  const { leaveRoom } = useSocket();

  const loser = gameState.players.find(p => p.id === gameState.loserId);
  const isLoser = clientState.myId === gameState.loserId;

  const [rouletteIndex, setRouletteIndex] = useState(0);
  const [hasVetoed, setHasVetoed] = useState(false);
  const rouletteRef = useRef<number | null>(null);

  // Determine stage based on GameState
  const isSpinning = gameState.isPenaltySpinning;
  const isRevealed = !!gameState.finalPenalty;
  const isVetoStage = !isSpinning && !isRevealed;

  // Identify the winner(s) - handle ties
  const sortedPlayers = [...gameState.players].sort((a, b) => b.score - a.score);
  const highestScore = sortedPlayers[0]?.score || 0;
  const winners = gameState.players.filter(p => p.score === highestScore);
  const winnerStakes = winners.map(w => w.suggestedPenalty.trim().toLowerCase());

  // Roulette animation
  useEffect(() => {
    if (isSpinning && gameState.penaltiesPool.length > 0) {
      let speed = 60;
      let count = 0;

      const tick = () => {
        setRouletteIndex(prev => (prev + 1) % gameState.penaltiesPool.length);
        count++;

        if (count > 30) speed += 20;

        if (speed < 500) {
          rouletteRef.current = window.setTimeout(tick, speed);
        } else {
          rouletteRef.current = window.setTimeout(tick, 500);
        }
      };

      tick();
    } else {
      if (rouletteRef.current) {
        clearTimeout(rouletteRef.current);
        rouletteRef.current = null;
      }
    }

    return () => {
      if (rouletteRef.current) clearTimeout(rouletteRef.current);
    };
  }, [isSpinning, gameState.penaltiesPool.length]);

  const handlePlayAgain = () => {
    playAgain();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full max-w-4xl flex flex-col items-center justify-center space-y-12"
    >
      {/* Leave Room Button */}
      <button
        onClick={leaveRoom}
        className="self-start text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors"
      >
        LEAVE ROOM
      </button>

      <AnimatePresence mode="wait">
        {/* VETO STAGE */}
        {isVetoStage && (
          <motion.div
            key="veto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="w-full space-y-8 text-center"
          >
            <div className="space-y-4">
              <h2 className="text-4xl font-black text-red-500 uppercase tracking-widest italic">
                THE MERCY RULE
              </h2>
              <div className="flex flex-col items-center">
                <span className="text-8xl">{loser?.avatar}</span>
                <h3 className="text-5xl font-black uppercase tracking-tight">{loser?.name}</h3>
                <p className="text-gray-400 font-bold uppercase tracking-widest text-xs mt-2">
                  {isLoser
                    ? "Pick ONE penalty to delete forever (Winner's stake is locked!)"
                    : 'Loser is choosing which penalty to delete...'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              {gameState.penaltiesPool.map((penalty, idx) => {
                const isWinnerStake = winnerStakes.includes(penalty.trim().toLowerCase());
                const canVeto = isLoser && !hasVetoed && !isWinnerStake;

                return (
                  <button
                    key={idx}
                    disabled={!canVeto}
                    onClick={() => {
                      setHasVetoed(true);
                      vetoPenalty(penalty);
                      if (navigator.vibrate) navigator.vibrate(100);
                    }}
                    className={`glass p-6 rounded-2xl font-bold text-lg min-w-[200px] border-b-4 transition-all relative ${
                      isWinnerStake
                        ? 'border-yellow-500 bg-yellow-500/10 cursor-not-allowed opacity-90'
                        : canVeto
                        ? 'hover:scale-105 hover:border-red-500 border-white/10 active:scale-95'
                        : 'border-white/5 opacity-50 cursor-default'
                    }`}
                  >
                    {isWinnerStake && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[10px] font-black px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                        <span>👑</span> PROTECTED
                      </div>
                    )}
                    "{penalty}"
                  </button>
                );
              })}
            </div>

            {isHost && (
              <div className="pt-8">
                <button
                  onClick={startPenaltyRoulette}
                  className="bg-neon-blue px-12 py-5 rounded-3xl font-black text-2xl shadow-xl hover:scale-105 active:scale-95 transition-all shadow-neon-blue/20"
                >
                  START ROULETTE
                </button>
              </div>
            )}
            {!isHost && (
              <div className="text-neon-blue font-black text-lg animate-pulse uppercase italic">
                Waiting for the bartender to spin the wheel...
              </div>
            )}
          </motion.div>
        )}

        {/* SPINNING STAGE */}
        {isSpinning && gameState.penaltiesPool.length > 0 && (
          <motion.div
            key="roulette"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full max-w-lg space-y-12 text-center"
          >
            <h2 className="text-5xl font-black uppercase tracking-tighter italic animate-bounce">
              SPINNING FATES
            </h2>
            <div className="glass p-12 rounded-[3rem] border-4 border-dashed border-neon-purple shadow-[0_0_50px_rgba(217,70,239,0.3)]">
              <motion.div
                key={rouletteIndex}
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-4xl font-black leading-tight"
              >
                "{gameState.penaltiesPool[rouletteIndex]}"
              </motion.div>
            </div>
            <div className="text-xs font-black text-gray-500 uppercase tracking-widest">
              Calculated globally. Panicking locally.
            </div>
          </motion.div>
        )}

        {/* REVEAL STAGE */}
        {isRevealed && (
          <motion.div
            key="reveal"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-2xl space-y-12 text-center"
          >
            <div className="space-y-4">
              <h2 className="text-2xl font-black text-red-500 uppercase tracking-[0.5em]">
                FINAL JUDGMENT
              </h2>
              <h1 className="text-8xl font-black italic tracking-tighter uppercase">{loser?.name}</h1>
              <span className="text-9xl block">{loser?.avatar}</span>
            </div>

            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="bg-white text-black p-12 rounded-[3rem] shadow-[0_0_100px_rgba(255,255,255,0.2)]"
            >
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">
                Must do
              </h3>
              <div className="text-5xl font-black leading-tight uppercase tracking-tighter">
                "{gameState.finalPenalty}"
              </div>
            </motion.div>

            <div className="flex flex-col items-center gap-6 pt-8">
              <button
                onClick={handlePlayAgain}
                className="bg-neon-purple px-12 py-5 rounded-2xl font-black text-2xl hover:scale-105 active:scale-95 transition-all"
              >
                PLAY AGAIN
              </button>
              <p className="text-gray-500 font-bold text-xs uppercase tracking-widest">
                Drink responsibly. Maybe.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PenaltyView;
